import React, { useEffect, useState } from 'react';
import { useClient } from '../connect_api';
import { UserService } from '../api/api_pb';
import { UserInfo, UserStatus } from '../api/api_pb';
import { toast } from 'react-toastify';
import { Shield, UserCheck, UserX, RefreshCw, Key, UserPlus } from 'lucide-react';
import RelativeTime from '../components/RelativeTime';
import ConfirmModal from '../components/ConfirmModal';
import AddUserModal from '../components/AddUserModal';

const AdminManagement: React.FC = () => {
  const userServiceClient = useClient(UserService);

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterStatus, setFilterStatus] = useState<UserStatus>(UserStatus.UNSPECIFIED);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    type: 'enable' | 'disable' | 'reset';
    user?: UserInfo;
  }>({ show: false, type: 'enable' });
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const limit = 20;

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await userServiceClient.listUsers({
        page: currentPage,
        limit,
        statusFilter: filterStatus,
      });

      setUsers(response.users);
      setTotalUsers(Number(response.total));
    } catch (error: any) {
      toast.error(`Failed to load users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus]);

  const handleUpdateStatus = async (userUuid: string, status: UserStatus) => {
    try {
      await userServiceClient.updateUserStatus({
        userUuid,
        status,
      });

      toast.success(`User ${status === UserStatus.ENABLED ? 'enabled' : 'disabled'} successfully`);
      loadUsers();
    } catch (error: any) {
      if (error.message.includes('cannot disable self')) {
        toast.error('You cannot disable your own account');
      } else {
        toast.error(`Failed to update user status: ${error.message}`);
      }
    }
  };

  const handleResetPassword = async (userUuid: string) => {
    try {
      const response = await userServiceClient.resetUserPassword({
        userUuid,
      });

      if (response.temporaryPassword) {
        toast.info(`Temporary password: ${response.temporaryPassword}`, {
          autoClose: 10000,
        });
      } else {
        toast.success('Password reset email sent successfully');
      }
    } catch (error: any) {
      if (error.message.includes('cannot reset own password')) {
        toast.error('You cannot reset your own password. Use the Change Password feature instead.');
      } else {
        toast.error(`Failed to reset password: ${error.message}`);
      }
    }
  };

  const handleConfirmAction = () => {
    if (!confirmModal.user) return;

    switch (confirmModal.type) {
      case 'enable':
        handleUpdateStatus(confirmModal.user.uuid, UserStatus.ENABLED);
        break;
      case 'disable':
        handleUpdateStatus(confirmModal.user.uuid, UserStatus.DISABLED);
        break;
      case 'reset':
        handleResetPassword(confirmModal.user.uuid);
        break;
    }

    setConfirmModal({ show: false, type: 'enable' });
  };

  const getStatusBadge = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ENABLED:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <UserCheck className="w-4 h-4" />
            Enabled
          </span>
        );
      case UserStatus.DISABLED:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <UserX className="w-4 h-4" />
            Disabled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const totalPages = Math.ceil(totalUsers / limit);

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">Admin User Management</h1>
        </div>
        <p className="text-gray-600">Manage admin accounts, permissions, and security settings</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(Number(e.target.value) as UserStatus);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value={UserStatus.UNSPECIFIED}>All Users</option>
            <option value={UserStatus.ENABLED}>Enabled Only</option>
            <option value={UserStatus.DISABLED}>Disabled Only</option>
          </select>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={loadUsers}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add User
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt && <RelativeTime timestamp={user.createdAt} />}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLoginAt ? (
                        <RelativeTime timestamp={user.lastLoginAt} />
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {user.status === UserStatus.ENABLED ? (
                          <button
                            onClick={() => setConfirmModal({ show: true, type: 'disable', user })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                            title="Disable User"
                          >
                            <UserX className="w-4 h-4" />
                            Disable
                          </button>
                        ) : (
                          <button
                            onClick={() => setConfirmModal({ show: true, type: 'enable', user })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                            title="Enable User"
                          >
                            <UserCheck className="w-4 h-4" />
                            Enable
                          </button>
                        )}

                        <button
                          onClick={() => setConfirmModal({ show: true, type: 'reset', user })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                          Reset Password
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, type: 'enable' })}
        onConfirm={handleConfirmAction}
        title={
          confirmModal.type === 'enable'
            ? 'Enable User Account'
            : confirmModal.type === 'disable'
            ? 'Disable User Account'
            : 'Reset User Password'
        }
        content={
          confirmModal.type === 'enable'
            ? `Are you sure you want to enable the account for ${confirmModal.user?.email}? They will be able to log in again.`
            : confirmModal.type === 'disable'
            ? `Are you sure you want to disable the account for ${confirmModal.user?.email}? They will not be able to log in until re-enabled.`
            : `Are you sure you want to reset the password for ${confirmModal.user?.email}? A temporary password will be generated or a reset email will be sent.`
        }
        confirmButtonText={confirmModal.type === 'disable' ? 'Disable' : confirmModal.type === 'enable' ? 'Enable' : 'Reset'}
        confirmButtonVariant={
          confirmModal.type === 'disable'
            ? 'bg-red-600 hover:bg-red-700'
            : 'bg-indigo-600 hover:bg-indigo-700'
        }
      />

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onUserCreated={loadUsers}
      />
    </div>
  );
};

export default AdminManagement;
